"use client";

import { useCallback, useMemo } from "react";
import {
  useQueries,
  useQuery,
  type QueryObserverResult,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { getUsageBySubscriptionId, validateEntitlement } from "@/actions/usage";
import type { EntitlementCheckModel, UsageModel } from "@/api";
import { isValidOrgId } from "@/lib/utils";

export function useSubscriptionUsage(
  subscriptionId: string | undefined,
  options?: Partial<UseQueryOptions<UsageModel[]>>,
) {
  return useQuery<UsageModel[]>({
    queryKey: ["usage", subscriptionId],
    queryFn: () => getUsageBySubscriptionId(subscriptionId as string),
    enabled: !!subscriptionId,
    staleTime: 60_000, // 1 min
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    ...(options ?? {}),
  });
}

export function useValidateEntitlement(
  orgId: string | undefined,
  name: string | undefined,
  options?: Partial<UseQueryOptions<EntitlementCheckModel>>,
) {
  return useQuery<EntitlementCheckModel>({
    queryKey: ["entitlement", orgId, name],
    queryFn: () => validateEntitlement(orgId as string, name as string),
    enabled: isValidOrgId(orgId) && !!name,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    ...(options ?? {}),
  });
}

export function useEntitlements(orgId: string | undefined) {
  const queries = useQueries({
    queries: [
      {
        queryKey: ["entitlement", orgId, "Daily Credits"],
        queryFn: () => validateEntitlement(orgId as string, "Daily Credits"),
        // Do not auto-run; fetch on demand to avoid server action POST spam
        enabled: false,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retry: false,
      },
      {
        queryKey: ["entitlement", orgId, "Monthly Credits"],
        queryFn: () => validateEntitlement(orgId as string, "Monthly Credits"),
        // Do not auto-run; fetch on demand to avoid server action POST spam
        enabled: false,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        retry: false,
      },
    ],
  });

  const daily = queries[0];
  const monthly = queries[1];

  const entitlementsOk = useMemo(() => {
    // Optimistic default: until we fetch, allow usage to avoid blocking UI
    if (!daily.data && !monthly.data) return true;
    const hasDaily = !!daily.data;
    const hasMonthly = !!monthly.data;
    const dailyOk =
      !hasDaily ||
      (!!daily.data?.isAvailable && (daily.data?.remaining ?? 0) > 0);
    const monthlyOk =
      !hasMonthly ||
      (!!monthly.data?.isAvailable && (monthly.data?.remaining ?? 0) > 0);

    return dailyOk && monthlyOk;
  }, [daily.data, monthly.data]);

  const isLoading = daily.isLoading || monthly.isLoading;
  const isFetching = daily.isFetching || monthly.isFetching;

  const refetchAll = useCallback(async (): Promise<
    [
      QueryObserverResult<EntitlementCheckModel, unknown>,
      QueryObserverResult<EntitlementCheckModel, unknown>,
    ]
  > => {
    return Promise.all([daily.refetch(), monthly.refetch()]);
  }, [daily.refetch, monthly.refetch]);

  return {
    daily,
    monthly,
    entitlementsOk,
    isLoading,
    isFetching,
    refetchAll,
  };
}
